'use client';

import 'react-day-picker/dist/style.css';
import './override.css';
import { de } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { DateRange, DayPicker } from 'react-day-picker';

import { useTranslation } from '@lichtblick/i18next';
import { CalendarMonthOutlined } from '@lichtblick/icons';
import { Colors, TextVariants } from '@lichtblick/theme';
import { formatPickerDate } from '@lichtblick/utils';

import { ButtonContainer, Container, DatePickerButton } from './DatePicker.styles';

import { Button } from '../Button';
import { PopoverComponent } from '../Popover';
import { Text } from '../Text';

type DatePickerProps = {
  date: DateRange | undefined | Date;
  error?: string | null;
  mode?: 'range' | 'single';
  setDate: (date: DateRange | undefined | Date) => void;
};

export const DatePicker = ({ date, error, mode = 'range', setDate }: DatePickerProps) => {
  const [currentDate, setCurrentDate] = useState<DateRange | Date | undefined>();
  const [month, setMonth] = useState<Date | undefined>(new Date());

  const [triggerLabel, setTriggerLabel] = useState<string>('Pick a date');
  const triggerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (mode === 'range' && date && 'from' in date) {
      const range: DateRange = {
        from: date.from ? new Date(date.from) : new Date(),
        to: date.to ? new Date(date.to) : undefined,
      };

      setCurrentDate(range);
      setMonth(range.from || new Date());
    } else if (mode === 'single' && date instanceof Date) {
      setCurrentDate(new Date(date as Date));
      setMonth(new Date(date as Date));
    }

    const label = formatPickerDate(date, t);

    setTriggerLabel(label);
  }, [date, t, mode]);

  return (
    <PopoverComponent
      align="start"
      content={
        <Container>
          {mode === 'range' ? (
            <>
              <DayPicker
                defaultMonth={month}
                locale={de}
                mode="range"
                onSelect={(range: DateRange | undefined) => setCurrentDate(range)}
                selected={currentDate as DateRange}
                showOutsideDays={true}
                styles={{
                  root: {
                    fontFamily: 'Roboto, sans-serif',
                  },
                  button: {
                    border: 'none',
                    outline: 'none',
                  },
                }}
              />
              <ButtonContainer>
                <Button
                  label={t('common:reset')}
                  onClick={() => {
                    setDate(undefined);
                    triggerRef.current?.click();
                  }}
                  variant="secondary"
                />
                <Button
                  label={t('common:ok')}
                  onClick={() => {
                    setDate(currentDate);
                    triggerRef.current?.click();
                  }}
                />
              </ButtonContainer>
            </>
          ) : (
            <DayPicker
              defaultMonth={month}
              locale={de}
              mode="single"
              onSelect={(date: Date | undefined) => {
                setCurrentDate(date);
                setDate(date);
                triggerRef.current?.click();
              }}
              selected={currentDate as Date}
              showOutsideDays={true}
              styles={{
                root: {
                  fontFamily: 'Roboto, sans-serif',
                },
                button: {
                  border: 'none',
                  outline: 'none',
                },
              }}
            />
          )}
        </Container>
      }
      trigger={
        <Container>
          <DatePickerButton $hasError={!!error && error !== ''} ref={triggerRef}>
            {triggerLabel}
            <CalendarMonthOutlined />
          </DatePickerButton>
          {error && error !== '' ? (
            <Text $variant={TextVariants.body.s} color={Colors.red[100]}>
              {error}
            </Text>
          ) : null}
        </Container>
      }
    />
  );
};
